import { Box, CircularProgress, MenuItem, Typography, makeStyles } from '@material-ui/core';
import ThreeDotMenu from './ThreeDotMenu';
import { useTranslation } from 'react-i18next';
import { ITheme, theme } from '../constants/theme';
import { textButtonHoverAndActive } from '../utils/styles/buttonStyles';
import { useHistory } from 'react-router-dom';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEffect, useState } from 'react';
import { MerchantSubscription, activateMerchantSubscription, suspendMerchantSubscription } from '../redux/actions/subscriptions';
import { SubscriptionActivateModal } from "./SubscriptionActivateModal";
import { SubscriptionSuspendModal } from "./SubscriptionSuspendModal";
import { AttachMoney } from '@material-ui/icons';

const useStyles = makeStyles((theme: ITheme) => ({
	card: {
		cursor: 'pointer',
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		padding: "24px 16px 24px 24px ",
		border: "1px solid rgba(0, 0, 0, 0.08)",
		boxSizing: "border-box",
		borderRadius: "20px",
		transition: "all 200ms ease",
		marginBottom: 16,
		...textButtonHoverAndActive
	},
	component: {
		display: 'flex',
		flexDirection: 'column',
		paddingLeft: 0,
		paddingRight: 0,
		marginTop: 24
	},
	active: {
		color: theme.palette.success.main,
		fontWeight: 700,
	},
	suspended: {
		color: theme.palette.warning.main,
		fontWeight: 700,
	},
	topTextContainer: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginRight: 16
	},
	creditLeft: {
		marginTop: 8,
	},
	monthlyPrice: {
		marginTop: 8,
		fontWeight: 700
	}
}));

enum SubscriptionCardModal {
	ADD_DELIVERY_TRANCHE,
	SUSPEND,
	ACTIVATE
}

interface ISubscriptionCardProps extends MerchantSubscription {
	refreshList: () => void;
}

export default function SubscriptionCardMerchant(props: ISubscriptionCardProps) {
	const {
		MerchantId,
		MerchantName,
		HasActiveSubscription,
		SubscriptionPrice,
		SubscriptionNumberOfCredits,
		refreshList
	} = props;
	const history = useHistory<{ from: string; }>();
	const classes = useStyles();
	const { t } = useTranslation();
	const [visibleModal, setVisibleModal] = useState<SubscriptionCardModal>();
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setLoading(false);
	}, [props.HasActiveSubscription]);

	function onClick() {
		let url = `/admin/subscription/merchant/${MerchantId}`;
		history.push(url);
	}

	function onCloseModal() {
		setVisibleModal(undefined);
		refreshList();
	}

	const onActivate = (numberOfCredits: number, price: number) => {
		setLoading(true);
		activateMerchantSubscription(
			{ MerchantId, NumberOfCredits: numberOfCredits, Price: Math.round(price * 100) },
			() => { onCloseModal(); },
			(err) => { }
		);
	};

	const onSuspend = () => {
		setLoading(true);
		suspendMerchantSubscription(
			{ MerchantId },
			() => { onCloseModal(); },
			(err) => { }
		);
	};

	return (
		<Box
			className={classes.card}>
			<Box
				className={classes.topTextContainer}
				onClick={onClick}>
				<Box>
					<Typography variant="h6" component="h2">
						{MerchantName}
					</Typography>
					{(HasActiveSubscription && SubscriptionPrice) ?
						<Typography variant="body2" className={classes.monthlyPrice}>
{t('currencySymbol')}{SubscriptionPrice ? (SubscriptionPrice / 100)?.toFixed(2) : "-"}/month
						</Typography>
						: null}
					{SubscriptionNumberOfCredits ?
						<>
							<Typography variant="body2" className={classes.creditLeft}>
								{SubscriptionNumberOfCredits} credits/month
							</Typography>
						</>
						: null}
				</Box>
				<Typography
					data-automation="promotion-code"
					variant="body2"
					className={HasActiveSubscription ? classes.active : classes.suspended}
					component="span">
					{loading ? <CircularProgress size={24} /> : HasActiveSubscription ? 'Active' : 'Inactive'}
				</Typography>
			</Box>
			<ThreeDotMenu>
				{HasActiveSubscription
					? <MenuItem
						style={{ color: theme.palette.error.main }}
						onClick={() => setVisibleModal(SubscriptionCardModal.SUSPEND)}>
						<DeleteIcon />
						<Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
							Suspend subscription
						</Typography>
					</MenuItem>
					: <MenuItem
						style={{ color: theme.palette.success.main }}
						onClick={() => setVisibleModal(SubscriptionCardModal.ACTIVATE)}>
						<AttachMoney />
						<Typography variant="body2" component="p" style={{ fontWeight: 'normal', paddingLeft: '16px' }}>
							Set subscription price
						</Typography>
					</MenuItem>
				}
			</ThreeDotMenu>

			<SubscriptionActivateModal
				isVisible={visibleModal === SubscriptionCardModal.ACTIVATE}
				close={onCloseModal}
				activate={onActivate}
				source="merchant"
			/>
			<SubscriptionSuspendModal
				isVisible={visibleModal === SubscriptionCardModal.SUSPEND}
				close={onCloseModal}
				suspend={onSuspend}
			/>
		</Box>
	);
}
